import { cx } from '@emotion/css';
import { forwardRef, useMemo } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DSHeading, DSText } from '@stihl-design-system/components';

import { PhoenixImage } from '@phoenix/core/frontend/shared/ui/assets/phoenix-image';

interface BaseBannerProps {
  id: string;
  backgroundImg: string;
  imageTags?: string[];
  headline?: string;
  description?: string | React.ReactNode;
  isAnimated?: boolean;
  className?: string;
}

export const BaseBanner = forwardRef<HTMLDivElement, React.PropsWithChildren<BaseBannerProps>>(
  ({ id, backgroundImg, imageTags, headline, description, isAnimated, className, children }, ref) => {
    const ImageContainer = useMemo(
      () =>
        isAnimated
          ? ({ children: imageContainerChildren }) => (
              <AnimatePresence>
                <motion.div
                  key={`${id}-background-${backgroundImg}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  aria-hidden="true"
                >
                  {imageContainerChildren}
                </motion.div>
              </AnimatePresence>
            )
          : ({ children: imageContainerChildren }) => imageContainerChildren,
      [backgroundImg, isAnimated, id]
    );

    return (
      <section
        ref={ref}
        aria-labelledby={`${id}-headline`}
        className={cx(
          'relative select-none hover:select-text text-center content-center min-h-[372px] tablet:min-h-[316px] desktop:min-h-[300px] py-11',
          className
        )}
        data-testid={id}
      >
        <ImageContainer>
          <PhoenixImage
            src={backgroundImg}
            priority={true}
            layout="fill"
            objectFit="cover"
            objectPosition="center"
            staticTags={imageTags}
          />
        </ImageContainer>
        <div className="relative z-10 rounded-lg bg-primary-white p-10 stihl-page-container desktop:max-w-[43rem]">
          {headline && (
            <DSHeading id={`${id}-headline`} size="large-uppercase">
              {headline}
            </DSHeading>
          )}
          <DSText className="mt-2 margin-auto">{description}</DSText>
          {children}
        </div>
      </section>
    );
  }
);

export default BaseBanner;
